import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { updatePrimaryPalette } from '@primeuix/themes';
import { StorageConstants } from '../constants/storage.constants';

export type AppTheme = 'light' | 'dark';

/** An accent-colour preset. `color` is the base brand colour; `dark` is the
 *  pressed/hover shade. `palette` is the full 50→950 ramp applied to PrimeNG's
 *  primary design tokens so its components recolour with the accent too. */
export interface AccentOption {
  key:     string;
  label:   string;
  color:   string;   // --app-primary
  dark:    string;   // --app-primary-dk
  palette: Record<string, string>;
}

/** A font preset. `stack` is the full CSS font-family value written to --app-font. */
export interface FontOption {
  key:   string;
  label: string;
  stack: string;
}

/**
 * Manages the application appearance: colour theme (Light / Dark), the brand
 * ACCENT colour, and the UI FONT. All three are user-selectable in Settings and
 * persisted in localStorage so they survive refresh and re-login.
 *
 * Mechanism
 * ─────────
 * - Theme: PrimeNG v20 Aura is configured with `darkModeSelector: '.app-dark'`
 *   (app-module.ts). Adding that class to <html> switches every PrimeNG
 *   component to its dark palette; custom styles react via
 *   `html.app-dark { --app-*: … }` in styles.scss.
 * - Accent: the chosen colour is written as an INLINE style on <html>
 *   (`--app-primary` / `--app-primary-dk`). An inline style on the element wins
 *   over both the `:root` and `html.app-dark` rules, so the accent is honoured
 *   in light AND dark mode. Clearing it (default accent) removes the inline
 *   override so the stylesheet defaults apply again.
 * - Font: the chosen font stack is written inline as `--app-font`; `html, body`
 *   use `font-family: var(--app-font, …)` so the whole app follows it.
 *
 * Startup
 * ──────
 * AppComponent calls applyStoredAppearance() in its constructor so the correct
 * theme + accent + font are applied before the first render (no flash).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  private readonly DARK_CLASS = 'app-dark';

  /** Accent presets offered in Settings. The first (blue) is the app default.
   *  Each `palette` is a Tailwind 50→950 ramp; `color` = 600, `dark` = 700. */
  readonly accents: AccentOption[] = [
    {
      key: 'blue', label: 'Blue', color: '#2563eb', dark: '#1d4ed8',
      palette: { 50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554' },
    },
    {
      key: 'indigo', label: 'Indigo', color: '#4f46e5', dark: '#4338ca',
      palette: { 50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc', 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3', 900: '#312e81', 950: '#1e1b4b' },
    },
    {
      key: 'violet', label: 'Violet', color: '#7c3aed', dark: '#6d28d9',
      palette: { 50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065' },
    },
    {
      key: 'teal', label: 'Teal', color: '#0d9488', dark: '#0f766e',
      palette: { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a', 950: '#042f2e' },
    },
    {
      key: 'green', label: 'Green', color: '#16a34a', dark: '#15803d',
      palette: { 50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d', 950: '#052e16' },
    },
    {
      key: 'amber', label: 'Amber', color: '#d97706', dark: '#b45309',
      palette: { 50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f', 950: '#451a03' },
    },
    {
      key: 'rose', label: 'Rose', color: '#e11d48', dark: '#be123c',
      palette: { 50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af', 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239', 900: '#881337', 950: '#4c0519' },
    },
    {
      key: 'slate', label: 'Slate', color: '#475569', dark: '#334155',
      palette: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617' },
    },
  ];

  /** Font presets offered in Settings. The first (Inter) is the app default.
   *  Web fonts (Inter, Roboto, Poppins, …) are loaded via a Google Fonts <link>
   *  in index.html; System UI and Georgia are OS-native. */
  readonly fonts: FontOption[] = [
    { key: 'inter',    label: 'Inter (default)', stack: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'system',   label: 'System UI',       stack: `system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'roboto',   label: 'Roboto',          stack: `"Roboto", "Segoe UI", Helvetica, Arial, sans-serif` },
    { key: 'poppins',  label: 'Poppins',         stack: `"Poppins", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'montserrat', label: 'Montserrat',    stack: `"Montserrat", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'opensans', label: 'Open Sans',       stack: `"Open Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'lato',     label: 'Lato',            stack: `"Lato", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'sourcesans', label: 'Source Sans 3', stack: `"Source Sans 3", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'worksans', label: 'Work Sans',       stack: `"Work Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'nunito',   label: 'Nunito Sans',     stack: `"Nunito Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'dmsans',   label: 'DM Sans',         stack: `"DM Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'manrope',  label: 'Manrope',         stack: `"Manrope", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'jakarta',  label: 'Plus Jakarta Sans', stack: `"Plus Jakarta Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'rubik',    label: 'Rubik',           stack: `"Rubik", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'mulish',   label: 'Mulish',          stack: `"Mulish", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'karla',    label: 'Karla',           stack: `"Karla", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'ibmplex',  label: 'IBM Plex Sans',   stack: `"IBM Plex Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'figtree',  label: 'Figtree',         stack: `"Figtree", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'spacegrotesk', label: 'Space Grotesk', stack: `"Space Grotesk", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'merriweather', label: 'Merriweather (serif)', stack: `"Merriweather", Georgia, "Times New Roman", Times, serif` },
    { key: 'playfair', label: 'Playfair Display (serif)', stack: `"Playfair Display", Georgia, "Times New Roman", Times, serif` },
    { key: 'lora',     label: 'Lora (serif)',    stack: `"Lora", Georgia, "Times New Roman", Times, serif` },
    { key: 'georgia',  label: 'Georgia (serif)', stack: `Georgia, "Times New Roman", Times, serif` },
    // ── Modern / geometric sans (distinct shapes) ──
    { key: 'sora',     label: 'Sora',            stack: `"Sora", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'outfit',   label: 'Outfit',          stack: `"Outfit", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'josefin',  label: 'Josefin Sans',    stack: `"Josefin Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'archivo',  label: 'Archivo',         stack: `"Archivo", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },

    // ── Rounded (friendly) ──
    { key: 'nunitorounded', label: 'Nunito',     stack: `"Nunito", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'quicksand', label: 'Quicksand',      stack: `"Quicksand", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'comfortaa', label: 'Comfortaa',      stack: `"Comfortaa", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'baloo',    label: 'Baloo 2',         stack: `"Baloo 2", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'fredoka',  label: 'Fredoka',         stack: `"Fredoka", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },

    // ── Condensed / display (headlines) ──
    { key: 'oswald',   label: 'Oswald',          stack: `"Oswald", "Arial Narrow", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'archivonarrow', label: 'Archivo Narrow', stack: `"Archivo Narrow", "Arial Narrow", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'bebas',    label: 'Bebas Neue',      stack: `"Bebas Neue", "Oswald", Impact, "Arial Narrow", sans-serif` },
    { key: 'abril',    label: 'Abril Fatface (display)', stack: `"Abril Fatface", Georgia, "Times New Roman", serif` },

    // ── Slab serif ──
    { key: 'robotoslab', label: 'Roboto Slab (slab)', stack: `"Roboto Slab", Georgia, "Times New Roman", serif` },
    { key: 'bitter',   label: 'Bitter (slab)',   stack: `"Bitter", Georgia, "Times New Roman", serif` },
    { key: 'zillaslab', label: 'Zilla Slab (slab)', stack: `"Zilla Slab", Georgia, "Times New Roman", serif` },

    // ── Elegant serif ──
    { key: 'cormorant', label: 'Cormorant Garamond (serif)', stack: `"Cormorant Garamond", Georgia, "Times New Roman", serif` },
    { key: 'spectral', label: 'Spectral (serif)', stack: `"Spectral", Georgia, "Times New Roman", serif` },
    { key: 'crimson',  label: 'Crimson Pro (serif)', stack: `"Crimson Pro", Georgia, "Times New Roman", serif` },
    { key: 'baskerville', label: 'Libre Baskerville (serif)', stack: `"Libre Baskerville", Georgia, "Times New Roman", serif` },

    // ── Handwriting / script ──
    { key: 'caveat',   label: 'Caveat (handwritten)', stack: `"Caveat", "Segoe Script", cursive` },
    { key: 'dancing',  label: 'Dancing Script (script)', stack: `"Dancing Script", "Segoe Script", cursive` },
    { key: 'pacifico', label: 'Pacifico (script)', stack: `"Pacifico", "Segoe Script", cursive` },
    { key: 'lobster',  label: 'Lobster (script)', stack: `"Lobster", "Segoe Script", cursive` },
    { key: 'comicneue', label: 'Comic Neue',     stack: `"Comic Neue", "Comic Sans MS", cursive` },

    // ── Techy / futuristic ──
    { key: 'exo2',     label: 'Exo 2',           stack: `"Exo 2", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },
    { key: 'orbitron', label: 'Orbitron (techno)', stack: `"Orbitron", "Segoe UI", Roboto, Helvetica, Arial, sans-serif` },

    // ── Classic / web-safe (OS-installed; lead with the native name, then a
    //    Google equivalent for cross-platform consistency, then a generic). ──
    { key: 'timesnewroman', label: 'Times New Roman', stack: `"Times New Roman", Tinos, Times, "Noto Serif", serif` },
    { key: 'georgiaclassic', label: 'Georgia',    stack: `Georgia, "Noto Serif", "Times New Roman", Times, serif` },
    { key: 'garamond', label: 'Garamond',         stack: `Garamond, "EB Garamond", "Palatino Linotype", "Book Antiqua", Palatino, serif` },
    { key: 'palatino', label: 'Palatino',         stack: `"Palatino Linotype", "Book Antiqua", Palatino, "PT Serif", Georgia, serif` },
    { key: 'ptserif',  label: 'PT Serif',         stack: `"PT Serif", Georgia, "Times New Roman", Times, serif` },
    { key: 'arial',    label: 'Arial',            stack: `Arial, Arimo, "Helvetica Neue", Helvetica, "Noto Sans", sans-serif` },
    { key: 'helvetica', label: 'Helvetica',       stack: `"Helvetica Neue", Helvetica, Arial, Arimo, "Noto Sans", sans-serif` },
    { key: 'verdana',  label: 'Verdana',          stack: `Verdana, Geneva, "DejaVu Sans", "Noto Sans", sans-serif` },
    { key: 'tahoma',   label: 'Tahoma',           stack: `Tahoma, Geneva, Verdana, "Noto Sans", sans-serif` },
    { key: 'trebuchet', label: 'Trebuchet MS',    stack: `"Trebuchet MS", "Segoe UI", Tahoma, "Noto Sans", sans-serif` },
    { key: 'calibri',  label: 'Calibri',          stack: `Calibri, "Segoe UI", "PT Sans", Candara, "Noto Sans", sans-serif` },
    { key: 'ptsans',   label: 'PT Sans',          stack: `"PT Sans", "Segoe UI", Arial, "Noto Sans", sans-serif` },
    { key: 'couriernew', label: 'Courier New',    stack: `"Courier New", Cousine, Courier, "Noto Sans Mono", monospace` },

    // ── Monospace ──
    { key: 'mono',     label: 'JetBrains Mono',  stack: `"JetBrains Mono", "Cascadia Code", "SFMono-Regular", Consolas, "Courier New", monospace` },
    { key: 'ibmplexmono', label: 'IBM Plex Mono', stack: `"IBM Plex Mono", "SFMono-Regular", Consolas, "Courier New", monospace` },
    { key: 'firacode', label: 'Fira Code',       stack: `"Fira Code", "Cascadia Code", "SFMono-Regular", Consolas, "Courier New", monospace` },
    { key: 'spacemono', label: 'Space Mono',     stack: `"Space Mono", "SFMono-Regular", Consolas, "Courier New", monospace` },
  ];

  private readonly _theme$  = new BehaviorSubject<AppTheme>(this.storedTheme());
  private readonly _accent$ = new BehaviorSubject<string>(this.storedAccentKey());
  private readonly _font$   = new BehaviorSubject<string>(this.storedFontKey());

  /** Observable of the currently active theme. */
  readonly theme$  = this._theme$.asObservable();
  /** Observable of the currently active accent key. */
  readonly accent$ = this._accent$.asObservable();
  /** Observable of the currently active font key. */
  readonly font$   = this._font$.asObservable();

  /** Synchronous snapshot of the current theme. */
  get currentTheme(): AppTheme { return this._theme$.value; }
  get isDark(): boolean { return this._theme$.value === 'dark'; }

  /** Synchronous snapshot of the current accent / font keys. */
  get currentAccent(): string { return this._accent$.value; }
  get currentFont(): string { return this._font$.value; }

  // ── Startup ──────────────────────────────────────────────────────────────

  /**
   * Apply the persisted theme + accent + font in one call. Call this once from
   * AppComponent's constructor so the appearance is correct before first render.
   */
  applyStoredAppearance(): void {
    this.apply(this.storedTheme(), false);
    this.applyAccent(this.storedAccentKey(), false);
    this.applyFont(this.storedFontKey(), false);
  }

  /** @deprecated kept for backward compatibility — use applyStoredAppearance(). */
  applyStoredTheme(): void { this.applyStoredAppearance(); }

  // ── Theme ──────────────────────────────────────────────────────────────────

  setTheme(theme: AppTheme): void { this.apply(theme, true); }
  toggleTheme(): void { this.setTheme(this.isDark ? 'light' : 'dark'); }

  // ── Accent ───────────────────────────────────────────────────────────────

  /** Switch to the given accent key, persist it, and emit to subscribers. */
  setAccent(key: string): void { this.applyAccent(key, true); }

  // ── Font ───────────────────────────────────────────────────────────────────

  /** Switch to the given font key, persist it, and emit to subscribers. */
  setFont(key: string): void { this.applyFont(key, true); }

  // ── Private ────────────────────────────────────────────────────────────────

  private apply(theme: AppTheme, persist: boolean): void {
    const html = document.documentElement;
    html.classList.toggle(this.DARK_CLASS, theme === 'dark');
    if (persist) localStorage.setItem(StorageConstants.THEME_KEY, theme);
    this._theme$.next(theme);
  }

  private applyAccent(key: string, persist: boolean): void {
    const html = document.documentElement;
    const accent = this.accents.find(a => a.key === key) ?? this.accents[0];
    const isDefault = accent.key === this.accents[0].key;

    // 1) Custom app chrome (var(--app-primary)): inline override on <html> so it
    //    beats both :root and html.app-dark. Default accent clears the override.
    if (isDefault) {
      html.style.removeProperty('--app-primary');
      html.style.removeProperty('--app-primary-dk');
    } else {
      html.style.setProperty('--app-primary', accent.color);
      html.style.setProperty('--app-primary-dk', accent.dark);
    }

    // 2) PrimeNG components: recolour the whole primary design-token palette at
    //    runtime so buttons, dropdowns, checkboxes, focus rings, etc. follow the
    //    accent. For the default (blue) this re-applies the same ramp the preset
    //    already uses, so it is a harmless no-op. Guarded so a future API change
    //    can never break theme application.
    try {
      updatePrimaryPalette(accent.palette);
    } catch {
      /* ignore — PrimeNG primary stays on the preset default */
    }

    if (persist) localStorage.setItem(StorageConstants.ACCENT_KEY, accent.key);
    this._accent$.next(accent.key);
  }

  private applyFont(key: string, persist: boolean): void {
    const html = document.documentElement;
    const font = this.fonts.find(f => f.key === key) ?? this.fonts[0];
    const isDefault = font.key === this.fonts[0].key;

    if (isDefault) {
      html.style.removeProperty('--app-font');
    } else {
      html.style.setProperty('--app-font', font.stack);
    }
    if (persist) localStorage.setItem(StorageConstants.FONT_KEY, font.key);
    this._font$.next(font.key);
  }

  private storedTheme(): AppTheme {
    return localStorage.getItem(StorageConstants.THEME_KEY) === 'dark' ? 'dark' : 'light';
  }

  private storedAccentKey(): string {
    const stored = localStorage.getItem(StorageConstants.ACCENT_KEY);
    return this.accents.some(a => a.key === stored) ? stored! : this.accents[0].key;
  }

  private storedFontKey(): string {
    const stored = localStorage.getItem(StorageConstants.FONT_KEY);
    return this.fonts.some(f => f.key === stored) ? stored! : this.fonts[0].key;
  }
}
